import { useContext } from 'react'

import { digObject, launchModal } from '@domain-group/javascript-utils'
import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as publicationActions from '@redux/modules/publication'

import defaultFormState, { requiredFields } from '@models/publication'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
// @ts-expect-error TS2724
import type { PublicationModel, PublicationRequestOptions } from '@models/types'

import PageContext from '@contexts/pageContext'

const stringifyObjectKeys = ['deadlines', 'features', 'options']

export const generateUrls = (publication?: Partial<PublicationModel>) => {
  const { id } = publication || {}

  return {
    editPublicationRelationsUrl: `#/systemManager/publications/${id}/edit/relations`,
    editPublicationUrl: `#/systemManager/publications/${id}/edit`,
    publicationsIndexUrl: '#/systemManager/publications',
  }
}

type CreatePublicationParams = {
  dispatch: AppDispatch,
  publicationParams: Partial<PublicationModel>,
  requestOptions?: PublicationRequestOptions,
}

const createPublication = (params: CreatePublicationParams) => {
  const { publicationParams, dispatch, requestOptions } = params
  const { createPublication: createFn } = publicationActions

  // @ts-expect-error TS2345
  const updatedParams = mergeAndStringifyEntityObjects({}, publicationParams, stringifyObjectKeys)

  return dispatch(createFn(updatedParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      // @ts-expect-error TS2339
      const { data } = response
      const { editPublicationUrl } = generateUrls(data)

      return {
        success,
        data,
        redirectUrl: editPublicationUrl,
      }
    }

    return response
  })
}

type UpdatePublicationParams = {
  dispatch: AppDispatch,
  publication: PublicationModel,
  publicationParams: Partial<PublicationModel>,
  requestOptions?: PublicationRequestOptions,
}

const updatePublication = (params: UpdatePublicationParams) => {
  const {
    publication, publicationParams, dispatch, requestOptions,
  } = params
  const { updatePublication: updateFn } = publicationActions

  // @ts-expect-error TS2345
  const updatedParams = mergeAndStringifyEntityObjects(publication, publicationParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams, requestOptions))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function usePublicationForm(
  publication: Partial<PublicationModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const publicationForm = useForm(
    defaultFormState,
    { entity: publication, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [publication.id, publication.cache_key],
  )

  return {
    ...publicationForm,
  }
}

export const useRelations = (publication: Partial<PublicationModel> = {}) => {
  const { provider_id } = publication || {}

  const providers = useSelector(reduxState => reduxState.entities.providers)

  const provider = provider_id && providers[provider_id] ? providers[provider_id] : {}

  return {
    provider,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: PublicationRequestOptions,
}

function usePublication(initEntity: Partial<PublicationModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: publication }: { entity: PublicationModel} = useLatestEntity(initEntity, 'publications')
  const { id } = publication

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { provider } = useRelations(publication)

  useReduxAction(
    'publications',
    'loadPublication',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.publications)

  const keyContacts = digObject(publication, 'options.key_contacts', [])
  const hasKeyContacts = !!keyContacts.length

  // @ts-expect-error TS7006
  const artworkContacts = keyContacts.filter(contact => contact.contact_type === 'artwork')
  const hasArtworkContacts = !!artworkContacts.length

  // @ts-expect-error TS7006
  const bookingContacts = keyContacts.filter(contact => contact.contact_type === 'booking')
  const hasBookingContacts = !!bookingContacts.length

  const logoUrl = digObject(publication, 'logo.thumb')

  const notesEnabled = digObject(publication, 'options.notes', false)
  const pagePositionEnabled = digObject(publication, 'options.page_position', 'false') === 'true'
  const revertPublicationOrderEnabled = digObject(publication, 'options.allow_user_revert', false) && !publication.booking_api

  return {
    artworkContacts,
    bookingContacts,
    callbacks: {
      createOrEditKeyContact: (keyContact: object) => launchModal({
        callbacks,
        modalKey: 'CreateOrEditKeyContactsModal',
        payload: {
          keyContact,
          publication,
        },
      }),
      createPublication: (publicationParams: Partial<PublicationModel>, entityOptions?: PublicationRequestOptions) => (
        createPublication({ publicationParams, dispatch, requestOptions: entityOptions })
      ),
      editPublicationDeliverySettings: () => launchModal({
        callbacks,
        modalKey: 'EditPublicationDeliverySettingsModal',
        payload: {
          publication,
        },
      }),
      editPublicationFTPSettings: () => launchModal({
        callbacks,
        modalKey: 'EditPublicationFileTransferSettingsModal',
        payload: {
          publication,
        },
      }),
      managePublicationDeadlines: () => launchModal({
        callbacks,
        modalKey: 'ManagePublicationDeadlinesModal',
        payload: {
          publication,
        },
      }),
      managePublicationPrintSpecifications: () => launchModal({
        callbacks,
        modalKey: 'ManagePublicationPrintSpecificationsModal',
        payload: {
          publication,
        },
      }),
      updatePublication: (publicationParams: Partial<PublicationModel>, entityOptions?: PublicationRequestOptions) => (
        updatePublication({
          publication, publicationParams, dispatch, requestOptions: entityOptions,
        })
      ),
      uploadLogo: () => launchModal({
        callbacks,
        modalKey: 'UploadPublicationLogoModal',
        payload: {
          publication,
        },
      }),
    },
    creating,
    deleting,
    hasArtworkContacts,
    hasBookingContacts,
    hasKeyContacts,
    keyContacts,
    // @ts-expect-error TS18048
    loading: loading && !loadedIds.includes(publication.id),
    logoUrl,
    notesEnabled,
    pagePositionEnabled,
    provider,
    publication,
    revertPublicationOrderEnabled,
    updating,
    urls: generateUrls(publication),
  }
}

export default usePublication
